import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Header } from '../components/layout/Header'
import { Card } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { Users, Activity, Database, Settings } from 'lucide-react'
import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

export const AdminPage: React.FC = () => {
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/admin/stats`)
      return data
    },
  })

  const { data: users } = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/admin/users`)
      return data
    },
  })

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
      <Header />

      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <h1 style={{ fontSize: '32px', marginBottom: '32px' }}>Admin Dashboard</h1>

        {/* System Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px', marginBottom: '32px' }}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Users</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats?.totalUsers || 0}</p>
              </div>
              <Users size={24} style={{ color: '#4A90E2' }} />
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>API Requests</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats?.apiRequests || 0}</p>
              </div>
              <Activity size={24} style={{ color: '#50C878' }} />
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>DB Queries</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats?.dbQueries || 0}</p>
              </div>
              <Database size={24} style={{ color: '#7B68EE' }} />
            </div>
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
              <div>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Cache Hit Rate</p>
                <p style={{ fontSize: '32px', fontWeight: 'bold' }}>{stats?.cacheHitRate || 0}%</p>
              </div>
              <Settings size={24} style={{ color: '#FF6B35' }} />
            </div>
          </Card>
        </div>

        {/* User Management */}
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '20px' }}>User Management</h3>
            <Button size="sm">Add User</Button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #E0E0E0' }}>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                    Name
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                    Email
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                    Role
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                    Last Login
                  </th>
                  <th style={{ textAlign: 'left', padding: '12px', fontSize: '14px', color: '#666', fontWeight: 500 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {users?.map((user: any) => (
                  <tr key={user.id} style={{ borderBottom: '1px solid #E0E0E0' }}>
                    <td style={{ padding: '12px' }}>{user.name}</td>
                    <td style={{ padding: '12px' }}>{user.email}</td>
                    <td style={{ padding: '12px' }}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: user.role === 'admin' ? '#E8F4FF' : '#F5F5F5',
                          color: user.role === 'admin' ? '#4A90E2' : '#666',
                        }}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>
                      {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* System Logs */}
        <Card style={{ marginTop: '24px' }}>
          <h3 style={{ fontSize: '20px', marginBottom: '16px' }}>Recent System Logs</h3>
          <div
            style={{
              backgroundColor: '#1A1A1A',
              borderRadius: '6px',
              padding: '16px',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '12px',
              color: '#0F0',
              maxHeight: '300px',
              overflowY: 'auto',
            }}
          >
            <div>[2024-01-15 10:32:15] INFO: API request completed in 45ms</div>
            <div>[2024-01-15 10:32:12] INFO: Cache hit for key: markets:europe</div>
            <div>[2024-01-15 10:32:10] INFO: Database query executed in 12ms</div>
            <div>[2024-01-15 10:32:05] INFO: User login: admin@expandly.com</div>
          </div>
        </Card>
      </main>
    </div>
  )
}
